import { useState, useMemo } from "react";
import { num, toM, ml, m2, money, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, AreaTable, ResultCard, useCatalog, ProductSelect } from "./shared";

function franjas(spaces, u, rolloAnchoM, auto) {
  let mlA = 0, mlB = 0;
  spaces.forEach((sp) => {
    const cant = Math.max(1, Math.round(num(sp.cant)) || 1);
    const L = toM(sp.largo, u), W = toM(sp.ancho, u);
    if (L <= 0 || W <= 0) return;
    mlA += Math.ceil(W / rolloAnchoM) * L * cant;
    mlB += Math.ceil(L / rolloAnchoM) * W * cant;
  });
  const useA = !auto ? true : mlA <= mlB;
  let mlv = 0, uni = 0;
  spaces.forEach((sp) => {
    const cant = Math.max(1, Math.round(num(sp.cant)) || 1);
    const L = toM(sp.largo, u), W = toM(sp.ancho, u);
    if (L <= 0 || W <= 0) return;
    const f = useA ? Math.ceil(W / rolloAnchoM) : Math.ceil(L / rolloAnchoM);
    const len = useA ? L : W;
    mlv += f * len * cant; uni += Math.max(0, f - 1) * len * cant;
  });
  return { ml: mlv, uniones: uni };
}

export default function PastoSintetico({ onSave, s }) {
  const items = useCatalog("pasto_sintetico");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("Residencial");
  const [superficie, setSuperficie] = useState("Tierra");
  const [spaces, setSpaces] = useState([{ nombre: "Área 1", largo: "6", ancho: "5", cant: "1" }]);
  const [obstaculos, setObstaculos] = useState([]);
  const [rolloAncho, setRolloAncho] = useState("4");
  const [pelo, setPelo] = useState("auto");
  const [desp, setDesp] = useState("8");
  const [arenaKg, setArenaKg] = useState("5");
  const [sacoKg, setSacoKg] = useState("25");
  const [adhRend, setAdhRend] = useState("5");
  const [precioM2, setPrecioM2] = useState("");
  const [precioInst, setPrecioInst] = useState("");
  const pr = prod ? prod.price : num(precioM2);
  const esDeportivo = tipo === "Deportivo" || tipo === "Putting Green";

  const r = useMemo(() => {
    const rolloAnchoM = num(rolloAncho) || 4;
    const areaTotal = spaces.reduce((a, sp) => a + toM(sp.largo, u) * toM(sp.ancho, u) * Math.max(1, Math.round(num(sp.cant)) || 1), 0);
    const areaObs = obstaculos.reduce((a, o) => a + toM(o.largo, u) * toM(o.ancho, u) * Math.max(1, Math.round(num(o.cant)) || 1), 0);
    const areaNeta = Math.max(0, areaTotal - areaObs);
    const { ml: mlTeorico, uniones } = franjas(spaces, u, rolloAnchoM, pelo === "auto");
    const mlAjust = mlTeorico * (1 + num(desp) / 100);
    const mlFinal = Math.ceil(mlAjust);
    const areaComprada = mlFinal * rolloAnchoM;
    const aprov = areaComprada > 0 ? (areaNeta / areaComprada) * 100 : 0;
    const arena = Math.ceil((areaNeta * num(arenaKg)) / Math.max(1, num(sacoKg)));
    const cintaUnion = uniones * 1.1;
    const adhesivo = num(adhRend) > 0 ? Math.ceil(areaNeta / num(adhRend)) : 0;
    const geotextil = superficie === "Tierra" ? areaNeta * 1.1 : 0;
    const perim = spaces.reduce((a, sp) => a + 2 * (toM(sp.largo, u) + toM(sp.ancho, u)) * Math.max(1, Math.round(num(sp.cant)) || 1), 0);
    const cMat = areaComprada * pr;
    const total = cMat + num(precioInst);
    const w = [];
    if (areaNeta === 0) w.push("Captura al menos un área con medidas.");
    if (aprov < 70 && areaNeta > 0) w.push(`Aprovechamiento bajo (${aprov.toFixed(0)}%): el ancho fijo del rollo genera merma.`);
    if (prod && prod.stock < areaComprada) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock} m², requieres ${areaComprada.toFixed(2)} m².`);
    const rows = [
      { section: "Proyecto" },
      { label: "Tipo de pasto", value: tipo }, { label: "Superficie de instalación", value: superficie },
      { label: "Ancho del rollo", value: ml(rolloAnchoM) }, { label: "Área total", value: m2(areaTotal) },
      { label: "Área descontada", value: m2(areaObs) }, { label: "Área neta", value: m2(areaNeta) },
      { section: "Cálculo (franjas)" },
      { label: "Metros lineales teóricos", value: ml(mlTeorico) }, { label: "Desperdicio aplicado", value: `${num(desp)}%` },
      { label: "Metros lineales a vender", value: ml(mlFinal), strong: true }, { label: "Área comprada", value: m2(areaComprada) },
      { label: "Aprovechamiento", value: `${aprov.toFixed(0)}%` },
      { section: "Complementos" },
      { label: "Arena sílica (sacos)", value: `${arena}` },
      ...(esDeportivo ? [{ label: "Relleno de caucho", value: "Requerido (uso deportivo)" }] : []),
      ...(cintaUnion > 0 ? [{ label: "Cinta de unión", value: ml(cintaUnion) }] : []),
      { label: "Adhesivo (unidades)", value: `${adhesivo}` },
      ...(geotextil ? [{ label: "Geotextil", value: m2(geotextil) }] : []),
      { label: "Perfiles de remate (perímetro)", value: ml(perim) },
      ...(superficie === "Tierra" ? [{ label: "Base / grava", value: "Requerida (sobre tierra)" }] : []),
      { section: "Costos" },
      { label: "Materiales", value: money(cMat) }, { label: "Instalación", value: money(num(precioInst)) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    return { rows, warnings: w, total, inputs: { u, tipo, superficie, rolloAncho } };
  }, [u, tipo, superficie, spaces, obstaculos, rolloAncho, pelo, desp, arenaKg, sacoKg, adhRend, pr, precioInst, prod, esDeportivo]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular pasto sintético?"><p>Se cubre en franjas según el ancho del rollo y la dirección del pelo. El sistema optimiza orientación, descuenta obstáculos y agrega arena sílica, geotextil, adhesivo, cinta y remates.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="ps-unidad" />
          <Sel label="Tipo de pasto" value={tipo} onChange={setTipo} opts={["Residencial", "Deportivo", "Putting Green", "Decorativo", "Alto tráfico"]} testid="ps-tipo" />
          <Sel label="Superficie de instalación" value={superficie} onChange={setSuperficie} opts={["Tierra", "Concreto", "Loseta", "Madera", "Deck", "Azotea", "Asfalto"]} testid="ps-superficie" />
          <Field label="Ancho del rollo (m)" value={rolloAncho} onChange={setRolloAncho} step="0.5" suffix="m" testid="ps-rolloancho" />
          <Sel label="Dirección del pelo" value={pelo} onChange={setPelo} opts={[["auto", "Automática (menor desperdicio)"], ["fija", "Fija (obligatoria)"]]} testid="ps-pelo" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="ps-product" />
        <AreaTable title="Áreas (largo × ancho)" rows={spaces} setRows={setSpaces} u={u} defaultName="Área" testid="ps-areas" />
        <AreaTable title="Obstáculos a descontar" rows={obstaculos} setRows={setObstaculos} u={u} addLabel="+ Obstáculo" defaultName="Obstáculo" testid="ps-obs" />
        <Advanced>
          <Field label="Desperdicio (%)" value={desp} onChange={setDesp} step="1" suffix="%" testid="ps-desp" />
          <Field label="Arena sílica (kg/m²)" value={arenaKg} onChange={setArenaKg} step="0.5" suffix="kg" testid="ps-arena" />
          <Field label="Saco de arena (kg)" value={sacoKg} onChange={setSacoKg} step="1" suffix="kg" testid="ps-saco" />
          <Field label="Rendimiento adhesivo (m²/unidad)" value={adhRend} onChange={setAdhRend} step="0.5" suffix="m²" testid="ps-adhrend" />
          <Field label="Precio por m² ($)" value={prod ? prod.price : precioM2} onChange={setPrecioM2} suffix="$" testid="ps-precio" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="ps-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Pasto Sintético — GOÛT" modality="pasto_sintetico" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
